/**
 * Type-safe access to Vite environment variables.
 *
 * All client-exposed env vars must be prefixed with VITE_.
 * Add new variables to the `schema` object and the `EnvVars` interface.
 */

interface EnvVars {
    VITE_API_URL: string;
    VITE_GRAPHQL_URL: string;
    VITE_SENTRY_DSN: string;
    VITE_SENTRY_ENVIRONMENT: string;
    /**
     * Which deployment this bundle is — `Prod`, `Stage`, `Test`, or `PR-243` for a preview.
     *
     * Taken from the stage rather than from `ENVIRONMENT_NAME`, because a preview environment
     * spreads `TEST_ENVIRONMENT` and would otherwise call itself Test. Empty in local
     * development, which reads as "not a deployed environment" and is treated like any other
     * non-production build.
     */
    VITE_ENVIRONMENT: string;
    VITE_LAUNCHDARKLY_CLIENT_ID: string;
    VITE_AUTH0_DOMAIN: string;
    VITE_AUTH0_CLIENT_ID: string;
    VITE_AUTH0_AUDIENCE: string;
    // AppSync live-chat subscriptions. Empty → subscriptions disabled (local dev).
    VITE_APPSYNC_GRAPHQL_URL: string;
    VITE_APPSYNC_REGION: string;
    VITE_WEB_APP_URL: string;
    /**
     * The Alumni sub-org whose admins get the alumni console. Baked in from `ALUMNI_SUB_ORG_ID` in
     * infrastructure/environments/env.<stage>.ts. Empty → nobody gets the console.
     */
    VITE_ALUMNI_SUB_ORG_ID: string;
}

type EnvKey = keyof EnvVars;

interface EnvVarConfig {
    required: boolean;
    default?: string;
}

const schema: Record<EnvKey, EnvVarConfig> = {
    VITE_API_URL: { required: true },
    VITE_GRAPHQL_URL: { required: false, default: "/graphql" },
    VITE_SENTRY_DSN: { required: false, default: "" },
    VITE_SENTRY_ENVIRONMENT: { required: false, default: "development" },
    VITE_ENVIRONMENT: { required: false, default: "" },
    VITE_LAUNCHDARKLY_CLIENT_ID: { required: false, default: "" },
    VITE_AUTH0_DOMAIN: { required: false, default: "" },
    VITE_AUTH0_CLIENT_ID: { required: false, default: "" },
    VITE_AUTH0_AUDIENCE: { required: false, default: "" },
    VITE_APPSYNC_GRAPHQL_URL: { required: false, default: "" },
    VITE_APPSYNC_REGION: { required: false, default: "us-east-1" },
    VITE_WEB_APP_URL: { required: false, default: "http://localhost:5174" },
    VITE_ALUMNI_SUB_ORG_ID: { required: false, default: "" },
};

function getEnvVar(key: EnvKey): string {
    const value = import.meta.env[key] as string | undefined;
    const config = schema[key];

    if (value !== undefined && value !== "") {
        return value;
    }

    if (config.required) {
        throw new Error(`Missing required environment variable: ${key}`);
    }

    return config.default ?? "";
}

/**
 * Convenience accessor for a single env var with a fallback default.
 * Used by modules that need a specific variable (e.g. Apollo link config).
 */
export function getEnv(key: EnvKey, fallback?: string): string {
    const value = import.meta.env[key] as string | undefined;
    if (value !== undefined && value !== "") {
        return value;
    }
    if (fallback !== undefined) {
        return fallback;
    }
    return schema[key]?.default ?? "";
}

/**
 * The environment this bundle was built for, or empty in production.
 *
 * Production is the one that must look like nothing special — a badge there is noise on the
 * screen people actually work in. Everywhere else says so, because the admin portal looks
 * identical in every environment and a partner's real roster is one indistinguishable tab away
 * from a test one. `VITE_ENVIRONMENT` carries the stage, so a preview reads `PR-243` rather than
 * inheriting Test's name.
 *
 * Empty in local development too, which is honest: a laptop is not a deployed environment, and
 * the page title and port already say where you are.
 *
 * Here rather than beside the badge that renders it, because it is a reading of an environment
 * variable and nothing else — every other one lives in this file, and testing it where it was
 * meant pulling the whole sidebar into a test about a string.
 */
const PRODUCTION_STAGE = "Prod";

/** Empty means "show nothing" — production, or a laptop. */
export const deployedEnvironment = (): string => {
    const stage = getEnv("VITE_ENVIRONMENT", "");
    return stage && stage !== PRODUCTION_STAGE ? stage : "";
};

export const env = {
    get apiUrl(): string {
        return getEnvVar("VITE_API_URL");
    },
    get graphqlUrl(): string {
        return getEnvVar("VITE_GRAPHQL_URL");
    },
    get sentryDsn(): string {
        return getEnvVar("VITE_SENTRY_DSN");
    },
    get sentryEnvironment(): string {
        return getEnvVar("VITE_SENTRY_ENVIRONMENT");
    },
    get launchDarklyClientId(): string {
        return getEnvVar("VITE_LAUNCHDARKLY_CLIENT_ID");
    },
    get auth0Domain(): string {
        return getEnvVar("VITE_AUTH0_DOMAIN");
    },
    get auth0ClientId(): string {
        return getEnvVar("VITE_AUTH0_CLIENT_ID");
    },
    get auth0Audience(): string {
        return getEnvVar("VITE_AUTH0_AUDIENCE");
    },
    get webAppUrl(): string {
        return getEnvVar("VITE_WEB_APP_URL");
    },
    get alumniSubOrgId(): string {
        return getEnvVar("VITE_ALUMNI_SUB_ORG_ID");
    },
    get isProd(): boolean {
        return import.meta.env.PROD;
    },
    get isDev(): boolean {
        return import.meta.env.DEV;
    },
} as const;
